import React, { useState, useEffect } from 'react';
import { Trophy, ChevronLeft, ChevronRight } from 'lucide-react';

/** Auto-playing banner slideshow. Accepts up to 4 banners; falls back to a
 * single legacy banner_file_id, then to a gradient placeholder. */
export function EventBannerCarousel({ banners = [], fallbackFileId = '', fileUrl, heightClass = 'h-64 sm:h-96' }) {
  const slides = (banners && banners.length > 0)
    ? banners
    : (fallbackFileId ? [{ file_id: fallbackFileId }] : []);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % slides.length), 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <div className={`${heightClass} w-full bg-gradient-to-br from-violet-500 via-fuchsia-500 to-rose-500 flex items-center justify-center rounded-none`} data-testid="event-banner-placeholder">
        <Trophy className="w-14 h-14 text-white/80" />
      </div>
    );
  }

  return (
    <div className={`relative ${heightClass} w-full overflow-hidden bg-slate-900`} data-testid="event-banner-carousel">
      {slides.map((s, i) => (
        <div key={s.file_id || i} className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}>
          {/* Blurred cover backdrop fills the frame, sharp image sits on top with
              object-contain so the full banner is always visible — never cropped. */}
          <img src={fileUrl(s.file_id)} alt="" className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-50" />
          <img src={fileUrl(s.file_id)} alt="" className="absolute inset-0 w-full h-full object-contain" />
        </div>
      ))}
      {slides.length > 1 && (
        <>
          <button
            onClick={() => setIndex((i) => (i - 1 + slides.length) % slides.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5"
            data-testid="banner-carousel-prev"
          ><ChevronLeft className="w-4 h-4" /></button>
          <button
            onClick={() => setIndex((i) => (i + 1) % slides.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5"
            data-testid="banner-carousel-next"
          ><ChevronRight className="w-4 h-4" /></button>
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === index ? 'bg-white w-5' : 'bg-white/50'}`}
                data-testid={`banner-carousel-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
